import type { HttpTransport as SharedHttpTransport } from '@mangovoice/api-client'
import type { ExtensionInstance, PaginatedApiResult } from '@mangovoice/types'

import type {
  CreateExtensionPayload,
  CreateSharedVoicemailPayload,
  MassEditLinesPayload,
  MobileDevice,
  UpdateCallQueuePayload,
  UpdateConferenceRoomPayload,
  UpdateExtensionPayload,
  UpdateRingGroupPayload,
  UserGroupOption,
  VoipProfile,
  VoipProfileRaw,
} from '../types/api.types'
import type { ExtensionsApiClient, ResetSipSecretResult } from './extensionsApi'

/** Per-request options forwarded to the underlying apisauce/axios client. */
export interface HttpRequestConfig {
  timeout?: number
  signal?: AbortSignal
}

/**
 * HTTP transport accepted by createExtensionsApiFromHttp.
 * Accepts both the full @mangovoice/api-client HttpTransport and simpler
 * apisauce-style transports that return `{ ok, data? }`.
 */
export type HttpTransport = SharedHttpTransport

function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach((v) => searchParams.append(key, String(v)))
      } else {
        searchParams.set(key, String(value))
      }
    }
  }
  return searchParams.toString()
}

function toApiResponse<T>(response: { ok: boolean; data?: unknown }): {
  data: T | null
  ok: boolean
} {
  return { data: (response.data as T) ?? null, ok: response.ok }
}

const toIntegerPayloadValue = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined
  if (typeof value === 'number' && Number.isFinite(value))
    return Math.trunc(value)
  if (typeof value === 'boolean') return value ? 1 : 0
  const parsed = Number.parseInt(String(value), 10)
  return Number.isNaN(parsed) ? undefined : parsed
}

const EXTENSION_TYPE_ROUTE_MAP: Record<string, string> = {
  ExtensionLine: 'extension-lines',
  ExtensionRingGroup: 'ring-groups',
  ExtensionCallQueue: 'call-queues',
  ExtensionConferenceRoom: 'conference-rooms',
  ExtensionVirtualFaxMachine: 'virtual-fax-machines',
  ExtensionSharedVoicemailBox: 'shared-voicemail-box',
}

/**
 * Build an `ExtensionsApiClient` from a raw HTTP transport.
 *
 * All URL construction, query-string building, type routing, and response
 * normalization live here — written once, consumed by every app.
 */
export function createExtensionsApiFromHttp(
  http: HttpTransport,
): ExtensionsApiClient {
  return {
    getExtensions: async (params) => {
      const qs = buildQueryString(params)
      const url = `/app/v1/extensions/${qs ? `?${qs}` : ''}`
      return toApiResponse<PaginatedApiResult<ExtensionInstance>>(
        await http.get(url),
      )
    },

    getExtensionInfo: async (extensionId) => {
      return toApiResponse<ExtensionInstance>(
        await http.get(`/app/v1/extensions/${extensionId}/`),
      )
    },

    createExtension: async (pbxId: number, payload: CreateExtensionPayload) => {
      const route = EXTENSION_TYPE_ROUTE_MAP[payload.type]
      if (!route) throw new Error(`Unknown extension type: ${payload.type}`)
      if (route === 'extension-lines') {
        const linePayload = payload as CreateExtensionPayload &
          Record<string, unknown>
        const assignedVoipProfileId = toIntegerPayloadValue(
          linePayload.assigned_voip_profile_id,
        )
        const assignedUserId = toIntegerPayloadValue(
          linePayload.assigned_user_id,
        )
        return toApiResponse<ExtensionInstance>(
          await http.post(`/app/v1/pbxes/${pbxId}/${route}/`, {
            ...linePayload,
            ...(assignedVoipProfileId !== undefined
              ? { assigned_voip_profile_id: assignedVoipProfileId }
              : assignedUserId !== undefined
                ? { assigned_user: assignedUserId }
                : {}),
          }),
        )
      }
      if (route === 'call-queues') {
        const callQueuePayload = payload as CreateExtensionPayload &
          Record<string, unknown>
        const periodicAnnouncementsEnable = toIntegerPayloadValue(
          callQueuePayload.periodic_announcements_enable,
        )
        return toApiResponse<ExtensionInstance>(
          await http.post(`/app/v1/pbxes/${pbxId}/${route}/`, {
            ...callQueuePayload,
            type: payload.type,
            ...(periodicAnnouncementsEnable !== undefined
              ? {
                  periodic_announcements_enable: periodicAnnouncementsEnable,
                }
              : {}),
          }),
        )
      }
      if (route === 'conference-rooms') {
        const conferencePayload = payload as CreateExtensionPayload &
          Record<string, unknown>
        const moderatorRequired = toIntegerPayloadValue(
          conferencePayload.moderator_required,
        )
        const conferencePinRequired = toIntegerPayloadValue(
          conferencePayload.conference_pin_required,
        )
        const participantsJoinMuted = toIntegerPayloadValue(
          conferencePayload.participants_join_muted,
        )
        const announceNewParticipants = toIntegerPayloadValue(
          conferencePayload.announce_new_participants,
        )
        return toApiResponse<ExtensionInstance>(
          await http.post(`/app/v1/pbxes/${pbxId}/${route}/`, {
            ...conferencePayload,
            type: payload.type,
            ...(moderatorRequired !== undefined
              ? { moderator_required: moderatorRequired }
              : {}),
            ...(conferencePinRequired !== undefined
              ? { conference_pin_required: conferencePinRequired }
              : {}),
            ...(participantsJoinMuted !== undefined
              ? { participants_join_muted: participantsJoinMuted }
              : {}),
            ...(announceNewParticipants !== undefined
              ? { announce_new_participants: announceNewParticipants }
              : {}),
          }),
        )
      }
      const { type: _type, ...createBody } = payload as CreateExtensionPayload &
        Record<string, unknown>
      return toApiResponse<ExtensionInstance>(
        await http.post(`/app/v1/pbxes/${pbxId}/${route}/`, createBody),
      )
    },

    createSharedVoicemail: async (
      pbxId: number,
      payload: CreateSharedVoicemailPayload,
    ) => {
      const sharedVoicemailPayload = payload as CreateSharedVoicemailPayload &
        Record<string, unknown>
      const omitFromDirectory = toIntegerPayloadValue(
        sharedVoicemailPayload.omit_from_directory,
      )
      const playVoicemailInstructions = toIntegerPayloadValue(
        sharedVoicemailPayload.play_voicemail_instructions,
      )
      const sendEmailNotifications = toIntegerPayloadValue(
        sharedVoicemailPayload.send_email_notifications,
      )
      const attachVoicemailMessage = toIntegerPayloadValue(
        sharedVoicemailPayload.attach_voicemail_message,
      )
      const deleteMessageAfterSend = toIntegerPayloadValue(
        sharedVoicemailPayload.delete_message_after_send,
      )
      const useSystemGreetings = toIntegerPayloadValue(
        sharedVoicemailPayload.use_system_greetings,
      )
      return toApiResponse<ExtensionInstance>(
        await http.post(`/app/v1/pbxes/${pbxId}/shared-voicemail-box/`, {
          ...sharedVoicemailPayload,
          type: 'ExtensionSharedVoicemailBox',
          ...(omitFromDirectory !== undefined
            ? { omit_from_directory: omitFromDirectory }
            : {}),
          ...(playVoicemailInstructions !== undefined
            ? { play_voicemail_instructions: playVoicemailInstructions }
            : {}),
          ...(sendEmailNotifications !== undefined
            ? { send_email_notifications: sendEmailNotifications }
            : {}),
          ...(attachVoicemailMessage !== undefined
            ? { attach_voicemail_message: attachVoicemailMessage }
            : {}),
          ...(deleteMessageAfterSend !== undefined
            ? { delete_message_after_send: deleteMessageAfterSend }
            : {}),
          ...(useSystemGreetings !== undefined
            ? { use_system_greetings: useSystemGreetings }
            : {}),
        }),
      )
    },

    deleteExtension: async (extensionId) => {
      return toApiResponse<void>(
        await http.delete(`/app/v1/extensions/${extensionId}/`),
      )
    },

    deleteAndReassignLine: async (
      pbxId,
      extensionId,
      reassignToExtensionId,
    ) => {
      return toApiResponse<void>(
        await http.post(
          `/app/v1/pbxes/${pbxId}/extension-lines/${extensionId}/delete-and-reassign/`,
          { reassign_to_extension_id: reassignToExtensionId },
        ),
      )
    },

    updateExtension: async (
      extensionId: number | string,
      payload: UpdateExtensionPayload,
    ) => {
      return toApiResponse<ExtensionInstance>(
        await http.patch(`/app/v1/extensions/${extensionId}/`, payload),
      )
    },

    updateRingGroup: async (
      pbxId: number,
      extensionId: number | string,
      payload: UpdateRingGroupPayload & {
        name?: string
        number?: string
        omit_from_directory?: boolean
      },
    ) => {
      return toApiResponse<ExtensionInstance>(
        await http.put(
          `/app/v1/pbxes/${pbxId}/ring-groups/${extensionId}/`,
          payload,
        ),
      )
    },

    updateCallQueue: async (
      pbxId: number,
      extensionId: number | string,
      payload: UpdateCallQueuePayload,
    ) => {
      const callQueuePayload = payload as UpdateCallQueuePayload &
        Record<string, unknown>
      const periodicAnnouncementsEnable = toIntegerPayloadValue(
        callQueuePayload.periodic_announcements_enable,
      )
      return toApiResponse<ExtensionInstance>(
        await http.put(`/app/v1/pbxes/${pbxId}/call-queues/${extensionId}/`, {
          ...callQueuePayload,
          ...(periodicAnnouncementsEnable !== undefined
            ? {
                periodic_announcements_enable: periodicAnnouncementsEnable,
              }
            : {}),
        }),
      )
    },

    updateConferenceRoom: async (
      pbxId: number,
      extensionId: number | string,
      payload: UpdateConferenceRoomPayload & {
        name?: string
        number?: string
        omit_from_directory?: boolean
      },
    ) => {
      const conferencePayload = payload as UpdateConferenceRoomPayload &
        Record<string, unknown>
      const moderatorRequired = toIntegerPayloadValue(
        conferencePayload.moderator_required,
      )
      const conferencePinRequired = toIntegerPayloadValue(
        conferencePayload.conference_pin_required,
      )
      const participantsJoinMuted = toIntegerPayloadValue(
        conferencePayload.participants_join_muted,
      )
      const announceNewParticipants = toIntegerPayloadValue(
        conferencePayload.announce_new_participants,
      )
      return toApiResponse<ExtensionInstance>(
        await http.put(
          `/app/v1/pbxes/${pbxId}/conference-rooms/${extensionId}/`,
          {
            ...conferencePayload,
            type: 'ExtensionConferenceRoom',
            ...(moderatorRequired !== undefined
              ? { moderator_required: moderatorRequired }
              : {}),
            ...(conferencePinRequired !== undefined
              ? { conference_pin_required: conferencePinRequired }
              : {}),
            ...(participantsJoinMuted !== undefined
              ? { participants_join_muted: participantsJoinMuted }
              : {}),
            ...(announceNewParticipants !== undefined
              ? { announce_new_participants: announceNewParticipants }
              : {}),
          },
        ),
      )
    },

    updateExtensionUserGroups: async (extensionId, payload) => {
      return toApiResponse<ExtensionInstance>(
        await http.put(
          `/app/v1/extensions/${extensionId}/user-groups/`,
          payload,
        ),
      )
    },

    resendSecureFaxLink: async (extensionId) => {
      return toApiResponse<void>(
        await http.post(
          `/app/v1/extensions/${extensionId}/resend-secure-fax-link/`,
          {},
        ),
      )
    },

    updateLinesLocation: async (params) => {
      return toApiResponse<{ updated: number }>(
        await http.post(
          `/app/v1/pbxes/${params.pbx_id}/extension-lines/bulk-update-location/`,
          params,
        ),
      )
    },

    massEditLines: async (
      params: MassEditLinesPayload & { pbx_id: number },
    ) => {
      return toApiResponse<{ updated: number }>(
        await http.post(
          `/app/v1/pbxes/${params.pbx_id}/extension-lines/mass-edit/`,
          params,
        ),
      )
    },

    resetSipSecret: async (pbxId, extensionId) => {
      return toApiResponse<ResetSipSecretResult>(
        await http.put(
          `/app/v1/pbxes/${pbxId}/extension-lines/${extensionId}/reset-sip-secret/`,
          {},
        ),
      )
    },

    clearDialNow: async (extensionId) => {
      return toApiResponse<void>(
        await http.post(
          `/app/v1/extensions/${extensionId}/clear-dial-now/`,
          {},
        ),
      )
    },

    getMobileDevices: async (extensionId) => {
      return toApiResponse<MobileDevice[]>(
        await http.get(`/app/v1/extensions/${extensionId}/mobile-devices/`),
      )
    },

    setLineActiveState: async (pbxId, extensionId, active) => {
      return toApiResponse<ExtensionInstance>(
        await http.post(
          `/app/v1/pbxes/${pbxId}/extension-lines/${extensionId}/set-active-state/`,
          { active },
        ),
      )
    },

    getPbxUserGroups: async (params) => {
      const qs = buildQueryString(params)
      const url = `/app/v1/pbxes/${params.pbx_id}/user-groups/${qs ? `?${qs}` : ''}`
      return toApiResponse<{ results: UserGroupOption[] }>(await http.get(url))
    },

    getLocations: async (params) => {
      const qs = buildQueryString(params)
      return toApiResponse<{ results: { id: number; name: string }[] }>(
        await http.get(
          `/app/v1/pbxes/${params.pbx_id}/locations/${qs ? `?${qs}` : ''}`,
        ),
      )
    },

    getSystemDefaults: async (params) => {
      return toApiResponse<{
        default_area_code?: string
        default_caller_id?: string
        default_time_zone?: string
      }>(await http.get(`/app/v1/pbx/${params.pbx_id}/system-settings/`))
    },

    getPlaylists: async (params) => {
      return toApiResponse<
        {
          id: number
          name: string
          is_default?: boolean
          type?: 'list' | 'stream'
          track_count?: number
          url?: string | null
        }[]
      >(await http.get(`/app/v1/pbxes/${params.pbx_id}/playlists/`))
    },

    getPhoneNumbers: async (params) => {
      const qs = buildQueryString({
        pbx_id: params.pbx_id,
        paginate: false,
        limit: 10000,
        offset: 0,
        ordering: 'name',
      })
      return toApiResponse<{
        results?: { name?: string; number?: string }[]
      }>(await http.get(`/app/v1/phone-numbers/?${qs}`))
    },

    getSoundClips: async (params) => {
      const qs = buildQueryString(params)
      const url = `/app/v1/sound-clips/${qs ? `?${qs}` : ''}`
      return toApiResponse<{ results?: { id: number; name: string }[] }>(
        await http.get(url),
      )
    },

    getUsers: async (params) => {
      const qs = buildQueryString({ pbx_id: params.pbx_id, paginate: false })
      // Extended timeout: the v2 list serializer has a per-user N+1 that is
      // slow on large PBXes and exceeds the default 10s client timeout. Uses
      // pbx_id scoping (unlike /users/select/, which scopes by the requesting
      // user's PBXes and hid users from cross-PBX admins).
      const response = toApiResponse<
        { id: number; full_name?: string; name?: string }[]
      >(await http.get(`/app/v2/users/?${qs}`, { timeout: 60 * 1000 }))

      if (response.ok && Array.isArray(response.data)) {
        return {
          ...response,
          data: response.data.map((u) => ({
            id: u.id,
            full_name: u.full_name || u.name || '',
          })),
        }
      }
      return response as {
        ok: boolean
        data: { id: number; full_name?: string }[] | null
      }
    },

    getVoipProfiles: async (params) => {
      // The voip-profiles endpoint paginates at 25/page by default. Pass
      // `paginate: false` (mirroring the old getUsers behavior) so the entire
      // profile list is returned in one call — otherwise the assignment
      // dropdown is silently truncated on large PBXes and edit-mode matching
      // can fail for profiles beyond the first page.
      const qs = buildQueryString({
        pbx_id: params.pbx_id,
        paginate: false,
        ...(params.subject_type ? { subject_type: params.subject_type } : {}),
      })
      // Extended timeout mirrors getUsers: the profile list maps ~1:1 to users
      // and is slow on large PBXes, so it can exceed the default ~10s client
      // timeout. A timeout would fail with ok:false → empty list → the
      // dropdown would show only "None".
      const response = toApiResponse<
        VoipProfileRaw[] | { results: VoipProfileRaw[] }
      >(await http.get(`/app/v1/voip-profiles/?${qs}`, { timeout: 60 * 1000 }))

      if (response.ok && response.data) {
        const data = response.data
        const rawProfiles = Array.isArray(data) ? data : (data.results ?? [])
        const profiles: VoipProfile[] = rawProfiles.map((p) => ({
          id: p.id,
          name: p.full_name || p.first_name || p.name || '',
          subject_type: p.subject_type,
          subject_id: p.subject_id,
        }))
        return { ok: true, data: profiles }
      }
      return { ok: response.ok, data: [] }
    },
  }
}
